import React from "react";
import ShowimagesLoading from "../../pageInside/showImagesLoading";
import { Link } from "react-router-dom";

// Heading font sizes for H1–H6 (used in preview mode)
const H_SIZE = [26, 22, 18, 16, 14, 13];

// Blue accent used for links and section badges in preview mode
const PREVIEW_BLUE = "#3b82f6";
const PREVIEW_FONT = "'Inter','Segoe UI',system-ui,sans-serif";

/*
 * Pages — renders an array of paragra items as a web page section.
 *
 * Props:
 *   preview    {boolean}  false (default) → CSS-class layout (Bootstrap grid),
 *                         true            → inline-styled layout (editor preview pane).
 *   classAlert {string}   outer wrapper class   (default: 'step-container')
 *   classPara  {string}   per-paragraph class   (default: 'step-card')
 *   classText  {string}   text column class     (default: 'step-text-col')
 *   classImg   {string}   media column class    (default: 'step-media-col')
 *   paragra    {object}   single paragraph data (used when paraALLBoo = true)
 *   paragra0   {array}    all paragraphs        (used when paraALLBoo = false)
 *   paraALLBoo {boolean}  true → render paragra, false → render paragra0
 *   stylePara  {array}    [headingLevel (1–6), listType ('1'|'a'|'A'|'i'|'I'|'ul')]
 *   imageVideo {string}   override for VORI field ('image' | 'video' | '')
 *   infoSource {object}   i18n source (optional)
 */
const Pages = (props) => {
    const {
        classAlert = 'step-container',
        classText = 'step-text-col',
        classImg = 'step-media-col',
        classPara = 'step-card',
        paragra = {},
        imageVideo = '',
        paragra0 = [],
        paraALLBoo = false,
        stylePara = [4, 'a'],
        infoSource,
        preview = false,
        contentPage = null,
    } = props;

    const __elakayText = (key, fallback = '') => {
        const val = infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.languagePagesIndex?.[key];
        return typeof val === 'string' ? val : fallback;
    };

    // ── shared helpers ─────────────────────────────────────────────────────────

    const normalizeParagraphs = (value) => {
        if (Array.isArray(value)) return value;
        if (!value || typeof value !== "object") return [];

        const paragraphLikeKeys = [
            "title", "text", "list", "VORI", "imagePath", "videoPath",
            "stylePara", "link", "href", "image", "video"
        ];
        const isSingleParagraph = paragraphLikeKeys.some((key) =>
            Object.prototype.hasOwnProperty.call(value, key)
        );
        if (isSingleParagraph) return [value];

        return Object.values(value).flatMap((entry) => {
            if (Array.isArray(entry)) return entry;
            return entry && typeof entry === "object" ? [entry] : [];
        });
    };

    const toDisplayString = (value) => {
        if (typeof value === "string") return value;
        if (typeof value === "number" || typeof value === "boolean") return String(value);
        return "";
    };

    const toMediaSrc = (value) => {
        const normalized = toDisplayString(value).trim();
        return normalized || "";
    };

    const stringToArray = (a) => {
        if (Array.isArray(a)) {
            return a.flatMap((item) => {
                const value = toDisplayString(item);
                if (!value) return [];
                return value.split(/\r?\n/).filter(Boolean);
            });
        }
        if (typeof a === "string") return a ? a.split(/\r?\n/).filter(Boolean) : [];
        if (typeof a === "number" || typeof a === "boolean") return [String(a)];
        return [];
    };

    const renderTitle = (t, n) => {
        const titleText = toDisplayString(t);
        if (!titleText) return null;
        const lvl = (n >= 1 && n <= 6) ? n : 4;
        if (preview) {
            return React.createElement(`h${lvl}`, {
                style: {
                    margin: "0 0 10px",
                    fontSize: H_SIZE[lvl - 1],
                    fontWeight: 700,
                    color: "#0f172a",
                    lineHeight: 1.3,
                    fontFamily: PREVIEW_FONT,
                }
            }, titleText);
        }
        return React.createElement(`h${lvl}`, null, titleText);
    };

    const renderList = (a, t = 'a') => {
        if (!a || a.length === 0) return null;
        const listSt = preview ? { paddingLeft: 22, margin: "8px 0 12px" } : {};
        const itemSt = preview ? {
            marginBottom: 4, color: "#334155", fontSize: 14,
            lineHeight: 1.6, fontFamily: PREVIEW_FONT,
        } : {};
        const items = a.map((item, i) => (
            <li key={i} style={itemSt} dangerouslySetInnerHTML={{ __html: toDisplayString(item) }} />
        ));
        if (t === 'ul') return <ul className="lp-list lp-list-unordered" style={listSt}>{items}</ul>;
        return <ol className="lp-list lp-list-ordered" type={t} style={listSt}>{items}</ol>;
    };

    const renderVideo = (src) => src && (
      src &&  <div style={preview ? { borderRadius: 6, overflow: "hidden", marginTop: 8 } : {}}>
            <video width="100%" controls autoPlay muted style={{ display: "block" }}>
                <source src={src} type="video/mp4" />
                {__elakayText("t001", "Your browser does not support the video tag.")}
            </video>
        </div>
    );

    const renderImage = (src) => src && (
      src &&  <div style={preview ? { borderRadius: 6, overflow: "hidden", marginTop: 8 } : {}}>
            <ShowimagesLoading
                style={{ width: "100%", display: "block" }}
                src={src}
                alt={__elakayText("t002", "Step illustration")}
            />
        </div>
    );

    const renderMedia = (vori, imgSrc, vidSrc) => {
        if (vori === 'image') return renderImage(imgSrc);
        if (vori === 'video') return renderVideo(vidSrc);
        return null;
    };

    const renderLinks = (linkObj, hrefObj) => {
        const internalHref = toDisplayString(linkObj?.href).trim();
        const externalHref = toDisplayString(hrefObj?.href).trim();
        const internalTitle = toDisplayString(linkObj?.title) || internalHref;
        const externalTitle = toDisplayString(hrefObj?.title) || externalHref;

        if (!internalHref && !externalHref) return null;
        if (preview) {
            return (
                <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {internalHref && (
                        <Link to={internalHref}
                            style={{ fontSize: 13, color: PREVIEW_BLUE, textDecoration: "underline", fontFamily: PREVIEW_FONT }}>
                            {internalTitle}
                        </Link>
                    )}
                    {externalHref && (
                        <a href={externalHref} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 13, color: PREVIEW_BLUE, textDecoration: "underline", fontFamily: PREVIEW_FONT }}>
                            {externalTitle}
                        </a>
                    )}
                </div>
            );
        }
        return (
            <div className={`${classText} lp-link-block`}>
                {internalHref && <Link to={internalHref}>{internalTitle}</Link>}
                {externalHref && (
                    <a href={externalHref} target="_blank" rel="noopener noreferrer">
                        {externalTitle}
                    </a>
                )}
            </div>
        );
    };

    // ── single paragraph (paraALLBoo = true) ──────────────────────────────────

    const { href, link, title, text, list, video, image } = paragra;
    const keyImVd = imageVideo || paragra.VORI;
    const valueImg = toMediaSrc(image?.src || paragra.imagePath);
    const valueVid = toMediaSrc(video?.src || paragra.videoPath);
    const hasMedia = !!(keyImVd && (valueImg || valueVid));
    const sp0 = Array.isArray(stylePara) ? stylePara[0] : 4;
    const sp1 = Array.isArray(stylePara) ? stylePara[1] : 'a';
    const textArr = stringToArray(text);
    const listArr = stringToArray(list);

    const viewParagra = () => {
        const mediaNode = renderMedia(keyImVd, valueImg, valueVid);

        if (preview) {
            return (
                <div style={{ fontFamily: PREVIEW_FONT, padding: "4px 0" }}>
                    {renderTitle(title, sp0)}
                    <div style={{
                        display: "flex", gap: 18, alignItems: "flex-start",
                        flexDirection: hasMedia ? "row" : "column",
                    }}>
                        <div style={{ flex: hasMedia ? "1 1 58%" : "1 1 100%", minWidth: 0 }}>
                            {textArr.map((a, i) => (
                                <p key={i}
                                    style={{ margin: "0 0 8px", color: "#334155", fontSize: 14, lineHeight: 1.7 }}
                                    dangerouslySetInnerHTML={{ __html: a }} />
                            ))}
                            {renderList(listArr, sp1)}
                        </div>
                        {hasMedia && (
                            <div style={{ flex: "0 0 38%", minWidth: 0 }}>
                                {mediaNode}
                            </div>
                        )}
                    </div>
                    {renderLinks(link, href)}
                </div>
            );
        }

        // Production — CSS class layout
        return (
            <div className={classPara}>
                <div className={classText}>
                    {renderTitle(title, sp0)}
                    {textArr.map((a, i) => (
                        <p key={i} dangerouslySetInnerHTML={{ __html: a }} />
                    ))}
                    {renderList(listArr, sp1)}
                </div>
                <div className={classImg}>
                    {mediaNode}
                </div>
                {renderLinks(link, href)}
            </div>
        );
    };

    // ── all paragraphs (paraALLBoo = false) ───────────────────────────────────

    const getBadgeTitle = (t) => {
        const titleText = toDisplayString(t);
        if (!titleText || titleText.length < 3) return null;
        const plain = titleText.replace(/<[^>]*>/g, '').trim();
        if (!plain) return null;
        return plain.length > 50 ? plain.substring(0, 47) + '...' : plain;
    };

    const usesArticleLayout = !preview && /\blp-section\b/.test(classPara || "");

    const renderTextParagraphs = (items, lead = false) =>
        items.map((a, i) => (
            <p
                key={i}
                className={lead && i === 0 ? "lp-lead" : "lp-paragraph"}
                dangerouslySetInnerHTML={{ __html: a }}
            />
        ));

    const viewAllParagraph = (obj, index) => {
        if (!obj || typeof obj !== "object") return null;
        const sp = Array.isArray(obj.stylePara) ? obj.stylePara : [4, 'a'];
        const tArr = stringToArray(obj.text);
        const lArr = stringToArray(obj.list);
        const imagePath = toMediaSrc(obj.imagePath);
        const videoPath = toMediaSrc(obj.videoPath);
        const hasMed = !!(obj.VORI && (imagePath || videoPath));
        const mediaNode = renderMedia(obj.VORI, imagePath, videoPath);
        const badgeTitle = getBadgeTitle(obj.title);

        if (preview) {
            return (
                <div key={index} style={{
                    borderBottom: "1px solid #e2e8f0",
                    paddingBottom: 18, marginBottom: 18,
                    fontFamily: PREVIEW_FONT,
                }}>
                    {/* Section number badge */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 20, height: 20, borderRadius: "50%",
                            background: PREVIEW_BLUE, color: "#fff",
                            fontSize: 10, fontWeight: 700, flexShrink: 0,
                        }}>
                            {index + 1}
                        </span>
                        {badgeTitle && (
                            <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                                {badgeTitle}
                            </span>
                        )}
                    </div>

                    {obj.title?.length > 3 && renderTitle(obj.title, sp[0])}

                    <div style={{
                        display: "flex", gap: 18, alignItems: "flex-start",
                        flexDirection: hasMed ? "row" : "column",
                    }}>
                        <div style={{ flex: hasMed ? "1 1 58%" : "1 1 100%", minWidth: 0 }}>
                            {tArr.map((a, i) => (
                                <p key={i}
                                    style={{ margin: "0 0 8px", color: "#334155", fontSize: 14, lineHeight: 1.7 }}
                                    dangerouslySetInnerHTML={{ __html: a }} />
                            ))}
                            {renderList(lArr, sp[1])}
                        </div>
                        {hasMed && (
                            <div style={{ flex: "0 0 38%", minWidth: 0 }}>
                                {mediaNode}
                            </div>
                        )}
                    </div>
                    {renderLinks(obj.link, obj.href)}
                </div>
            );
        }

        if (usesArticleLayout) {
            const sectionClass = [
                classPara,
                badgeTitle ? "has-badge" : "no-badge",
                hasMed ? "with-media" : "text-only",
            ].join(" ");

            return (
                <section
                    key={index}
                    id={`lp-section-${index}`}
                    className={sectionClass}
                    data-step={badgeTitle || ''}
                >
                    <div className="lp-section-title-row">
                        <span className="lp-section-index">{String(index + 1).padStart(2, '0')}</span>
                        <div className="lp-section-title-wrap">
                            {obj.title?.length > 3 && renderTitle(obj.title, sp[0])}
                        </div>
                    </div>

                    <div className={`lp-section-content-grid ${hasMed ? "has-media" : "no-media"}`}>
                        <div className={`${classText} lp-text-stack`}>
                            {renderTextParagraphs(tArr, true)}
                            {renderList(lArr, sp[1])}
                        </div>
                        {hasMed && (
                            <div className={classImg}>
                                {mediaNode}
                            </div>
                        )}
                    </div>

                    {renderLinks(obj.link, obj.href)}
                </section>
            );
        }

        // Production — CSS class layout
        return (
            <div
                key={index}
                id={`lp-section-${index}`}
                className={`${classPara} ${badgeTitle ? 'has-badge' : 'no-badge'}`}
                data-step={badgeTitle || ''}
            >
                <div className={classText}>
                    {obj.title?.length > 3 && renderTitle(obj.title, sp[0])}
                    {renderTextParagraphs(tArr)}
                    {renderList(lArr, sp[1])}
                </div>
                <div className={classImg}>
                    {mediaNode}
                </div>
                {renderLinks(obj.link, obj.href)}
            </div>
        );
    };

    // ── render ─────────────────────────────────────────────────────────────────
    const contentPageSections = Array.isArray(contentPage?.sections) ? contentPage.sections : null;
    const allParagraphs = normalizeParagraphs(contentPageSections?.length ? contentPageSections : paragra0);

    return (
        <div className={classAlert}>
            {paraALLBoo
                ? viewParagra()
                : allParagraphs.map((ob, j) => viewAllParagraph(ob, j))
            }
        </div>
    );
};

export default Pages;
